import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "../api/axios";
import {
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import toast from "react-hot-toast";
import { LoadingCard } from "../components/LoadingSpinner";
import "./OrgCert.css";
import * as XLSX from "xlsx";

const CHART_COLORS = {
  provider: "#38bdf8",
  difficulty: "#a855f7",
  location: "#22d3ee",
};

// US state abbreviations for location mapping
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

// Normalize location to standardized groups
function normalizeLocation(rawLocation) {
  if (!rawLocation) return "Other";
  
  const loc = rawLocation.trim();
  const upperLoc = loc.toUpperCase();
  const lowerLoc = loc.toLowerCase();
  
  // Indian locations
  if (loc.includes("Bengaluru") || loc.includes("BL-")) return "Bengaluru";
  if (loc.includes("Chennai") || loc.includes("CH-") || loc.includes("Perungudi")) return "Chennai";
  if (loc.includes("Hyderabad")) return "Hyderabad";
  if (loc.includes("Coimbatore")) return "Coimbatore";

  // UK and Ireland locations (keep before US mapping)
  if (
    lowerLoc.includes("england") ||
    lowerLoc.includes("london") ||
    lowerLoc.includes("farringdon")
  ) {
    return "England";
  }

  if (lowerLoc.includes("ireland") || lowerLoc.includes("dublin")) {
    return "Ireland";
  }

  // Check if it's a US location (contains state abbreviation)
  const hasUSState = US_STATES.some((state) => {
    const statePattern = new RegExp(`(^|[\\s,-])${state}($|[\\s,-])`);
    return statePattern.test(upperLoc);
  });
  if (hasUSState) return "US";
  
  return loc; // Keep original if no match
}

export default function OrgCertDashboard() {
  const [catalog, setCatalog] = useState([]);
  const [allCerts, setAllCerts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [filterCertIds, setFilterCertIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState("Registered");
  const [filterLocations, setFilterLocations] = useState([]);
  const [filterProviders, setFilterProviders] = useState([]);
  const [filterDifficulties, setFilterDifficulties] = useState([]);
  const [sortBy, setSortBy] = useState("points");
  const [sortOrder, setSortOrder] = useState("desc");

  // PAGINATION
  const [page, setPage] = useState(1);
  const pageSize = 10; // rows per page

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [catRes, certRes, locRes] = await Promise.all([
          api.get("/certs/catalog"),
          api.get("/certs/all"),
          api.get("/users/locations"),
        ]);
        setCatalog(catRes.data || []);
        
        // Normalize locations in certification data
        const normalizedCerts = (certRes.data || []).map(cert => ({
          ...cert,
          location: normalizeLocation(cert.location)
        }));
        setAllCerts(normalizedCerts);
        
        // Get unique normalized locations
        const normalizedLocs = [...new Set(
          (locRes.data || []).map(normalizeLocation)
        )].sort();
        setLocations(normalizedLocs);
      } catch (err) {
        console.error("Failed to load dashboard:", err);
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const certMap = useMemo(() => {
    const m = {};
    catalog.forEach((c) => (m[c.certId] = c));
    return m;
  }, [catalog]);

  const providerOptions = useMemo(() => {
    return [...new Set(catalog.map((c) => c.provider).filter(Boolean))].sort();
  }, [catalog]);

  const difficultyOptions = useMemo(() => {
    const values = catalog
      .map((c) => normalizeDifficulty(c.difficulty || c.level))
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) => {
      const order = ["fundamental", "associate", "professional", "specialty"];
      const aIndex = order.indexOf(a);
      const bIndex = order.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [catalog]);

  const filtered = useMemo(() => {
    let result = allCerts.filter((c) => {
      if (filterCertIds.length && !filterCertIds.includes(c.certId)) return false;
      if (filterStatus === "Completed" && c.status !== "Completed") return false;
      if (filterStatus === "Registered" && !["Registered", "Completed"].includes(c.status)) return false;
      if (filterStatus === "Ongoing" && c.status === "Completed") return false;
      if (filterLocations.length && !filterLocations.includes(c.location)) return false;

      const meta = certMap[c.certId] || {};
      const provider = meta.provider || "";
      const difficulty = normalizeDifficulty(meta.difficulty || meta.level);

      if (filterProviders.length && !filterProviders.includes(provider)) return false;
      if (filterDifficulties.length && !filterDifficulties.includes(difficulty)) return false;

      return true;
    });

    // Sorting logic
    result.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "points":
          aValue = Number(a.points) || 0;
          bValue = Number(b.points) || 0;
          break;
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "completedDate":
          aValue = new Date(a.completedDate || "1900-01-01");
          bValue = new Date(b.completedDate || "1900-01-01");
          break;
        case "location":
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.points;
          bValue = b.points;
      }

      return sortOrder === "asc"
        ? aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        : aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });

    return result;
  }, [
    allCerts,
    certMap,
    filterCertIds,
    filterStatus,
    filterLocations,
    filterProviders,
    filterDifficulties,
    sortBy,
    sortOrder,
  ]);

  // -------------------------
  // COMBINED TOP PERFORMERS
  // -------------------------
  const stats = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((c) => c.status === "Completed").length;

    // group by provider
    const byProvider = providerOptions.reduce((acc, provider) => {
      acc[provider] = 0;
      return acc;
    }, {});
    filtered.forEach((c) => {
      const meta = certMap[c.certId];
      if (!meta) return;
      const provider = meta.provider;
      if (!provider) return;
      if (byProvider[provider] == null) byProvider[provider] = 0;
      byProvider[provider] += 1;
    });

    // group by location
    const byLocation = locations.map((loc) => ({
      location: loc,
      count: filtered.filter((c) => c.location === loc).length,
    }));

    // group by difficulty
    const byDifficulty = difficultyOptions.reduce((acc, difficulty) => {
      acc[difficulty] = 0;
      return acc;
    }, {});
    filtered.forEach((c) => {
      const meta = certMap[c.certId];
      if (!meta) return;
      const difficulty = normalizeDifficulty(meta.difficulty || meta.level);
      if (!difficulty) return;
      if (byDifficulty[difficulty] == null) byDifficulty[difficulty] = 0;
      byDifficulty[difficulty] += 1;
    });

const performerMap = {};

filtered.forEach((c) => {
  if (!performerMap[c.employeeId]) {
    performerMap[c.employeeId] = {
      employeeId: c.employeeId,
      name: c.name,
      location: c.location,
      totalPoints: 0,
      totalBonus: 0,
    };
  }

  // Add base points for ALL statuses (Registered or Completed)
  performerMap[c.employeeId].totalPoints += Number(c.points) || 0;

  // Add bonus only for completed ones
  if (c.status === "Completed") {
    performerMap[c.employeeId].totalBonus += Number(c.bonusPoints) || 0;
  }
});

const topPerformers = Object.values(performerMap)
  .sort((a, b) => b.totalPoints - a.totalPoints)
  .slice(0, 5);

    const totalPointsEarned = Object.values(performerMap).reduce(
      (sum, p) => sum + p.totalPoints,
      0
    );

    return {
      total,
      completed,
      byProvider,
      byDifficulty,
      byLocation,
      topPerformers,
      totalPointsEarned,
    };
  }, [filtered, certMap, locations, providerOptions, difficultyOptions]);

  // Provider chart
  const providerChartData = providerOptions
    .map((key) => ({
      provider: key,
      count: stats.byProvider[key] || 0,
    }))
    .sort((a, b) => b.count - a.count);

  const difficultyChartData = difficultyOptions
    .map((key) => ({
      difficulty: toTitleCase(key),
      count: stats.byDifficulty[key] || 0,
    }))
    .sort((a, b) => b.count - a.count);

  // PAGINATION CALC
  const totalPages = Math.ceil(filtered.length / pageSize);

  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);
  function exportToExcel() {
    if (!filtered.length) {
      toast.error("No data to export");
      return;
    }
  
    const rows = filtered.map((c) => {
      const meta = certMap[c.certId] || {};
  
      return {
        Employee: c.name || "",
        Email: c.email || c.employeeId || "",
        Location: c.location || "",
        Certificate: meta.name || c.certId,
        Provider: meta.provider || "",
        Status: c.status || "",
        "Completion Date": c.completedDate || "",
      };
    });
  
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
  
    XLSX.utils.book_append_sheet(workbook, worksheet, "Certificates");
  
    XLSX.writeFile(workbook, "Org_Certifications.xlsx");
  }
  
  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <LoadingCard message="Loading organization dashboard..." />
      </div>
    );
  }
  
  return (
    <div style={{ width: "100%", background: "rgba(3,19,40,0.95)" }}>
    <div className="container-fluid">
    {/* // <div style={{ padding: "0px 20px", width: "100%" }}> */}


      {/* ---------------- FILTER PANEL ---------------- */}
      <div className="card mb-3" style={cardStyle}>
        <h5 style={{ color: "#eaf6ff" }}>Organization Certifications Overview</h5>

        <div className="d-flex flex-wrap gap-2 mt-3 filter-panel">
          
          {/* Cert Name */}
          <MultiSelectFilter
            allLabel="All Certifications"
            selectedValues={filterCertIds}
            options={catalog
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => ({
                value: c.certId,
                label: `${c.name} (${c.provider})`,
              }))}
            onChange={(values) => {
              setFilterCertIds(values);
              setPage(1);
            }}
            maxWidth={280}
          />

          {/* Status */}
          <SingleSelectFilter
            value={filterStatus}
            options={[
              { value: "Registered", label: "Registered" },
              { value: "Ongoing", label: "Ongoing" },
              { value: "Completed", label: "Completed" },
            ]}
            onChange={(value) => {
              setFilterStatus(value);
              setPage(1);
            }}
            maxWidth={180}
          />

          {/* Location */}
          <MultiSelectFilter
            allLabel="All Locations"
            selectedValues={filterLocations}
            options={locations.map((l) => ({ value: l, label: l }))}
            onChange={(values) => {
              setFilterLocations(values);
              setPage(1);
            }}
            maxWidth={200}
          />

          {/* Provider */}
          <MultiSelectFilter
            allLabel="All Providers"
            selectedValues={filterProviders}
            options={providerOptions.map((p) => ({ value: p, label: p }))}
            onChange={(values) => {
              setFilterProviders(values);
              setPage(1);
            }}
            maxWidth={200}
          />

          {/* Difficulty */}
          <MultiSelectFilter
            allLabel="All Difficulty"
            selectedValues={filterDifficulties}
            options={difficultyOptions.map((d) => ({
              value: d,
              label: toTitleCase(d),
            }))}
            onChange={(values) => {
              setFilterDifficulties(values);
              setPage(1);
            }}
            maxWidth={200}
          />

          {/* Sorting */}
          <SingleSelectFilter
            value={sortBy}
            options={[
              { value: "points", label: "Sort by Points" },
              { value: "name", label: "Sort by Name" },
              { value: "completedDate", label: "Sort by Date" },
              { value: "location", label: "Sort by Location" },
              { value: "status", label: "Sort by Status" },
            ]}
            onChange={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            maxWidth={180}
          />

        </div>
      </div>

      {/* ---------------- SUMMARY CARDS ---------------- */}
      <div className="row g-3">
        <StatCard title="Total Certifications Enrolled" value={stats.total} />
        <StatCard title="Completed" value={stats.completed} />
        <StatCard title="Total Points Earned" value={stats.totalPointsEarned.toLocaleString()} />
      </div>

      {/* ---------------- TOP PERFORMERS ---------------- */}
      {stats.topPerformers.length > 0 && (
        <div className="card mt-4" style={cardStyle}>
          <h6 style={cardTitle}>🏆 Top Performers (Certification only - Enrolled and Completed )</h6>
          <div className="row g-2">
            {stats.topPerformers.map((p, index) => (
              <div key={p.employeeId} className="col-md-2">
                <div
                  style={{
                    ...cardStyle,
                    padding: 12,
                    textAlign: "center",
                    background:
                      index === 0
                        ? "rgba(255, 193, 7, 0.1)"
                        : "rgba(0, 201, 255, 0.05)",
                    border:
                      index === 0
                        ? "1px solid rgba(255, 193, 7, 0.5)"
                        : "1px solid rgba(0, 201, 255, 0.2)",
                  }}
                >
                  <div style={{ fontSize: 11, color: "#8ecbff" }}>
                    {index === 0 ? "👑 Rank 1" : `#${index + 1}`}
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 600, color: "#eaf6ff" }}>
                    {p.name}
                  </div>

                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: "bold",
                      color: index === 0 ? "#ffc107" : "#00c9ff",
                    }}
                  >
                    {p.totalPoints} pts
                  </div>

                  {p.totalBonus > 0 && (
                    <div style={{ fontSize: 10, color: "#8ecbff" }}>
                      +{p.totalBonus} bonus
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- PROVIDER + LOCATION CHARTS ---------------- */}
      <div className="row mt-4 g-3 charts-row">
        
        {/* Provider */}
        <div className="col-lg-6">
          <div className="card" style={{ ...cardStyle, minHeight: 360 }}>
            <h6 style={cardTitle}>By Provider</h6>
            {providerChartData.length === 0 ? (
              <div style={{ color: "#8ecbff", fontSize: 13 }}>No data found</div>
            ) : (
              <div style={{ height: 300, overflowY: "auto" }}>
                <div style={{ height: Math.max(300, providerChartData.length * 34) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={providerChartData} layout="vertical" margin={{ left: 8, right: 32 }}>
                      <XAxis
                        type="number"
                        stroke="#8ecbff"
                        allowDecimals={false}
                        domain={[0, (max) => Math.max(4, Math.ceil(max * 1.15))]}
                      />
                      <YAxis
                        type="category"
                        dataKey="provider"
                        stroke="#8ecbff"
                        width={110}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill={CHART_COLORS.provider} radius={[0, 6, 6, 0]} barSize={14}>
                        <LabelList dataKey="count" position="right" fill="#8ecbff" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Difficulty */}
        <div className="col-lg-6">
          <div className="card" style={{ ...cardStyle, minHeight: 360 }}>
            <h6 style={cardTitle}>By Difficulty</h6>
            {difficultyChartData.length === 0 ? (
              <div style={{ color: "#8ecbff", fontSize: 13 }}>No data found</div>
            ) : (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={difficultyChartData} layout="vertical" margin={{ left: 8, right: 32 }}>
                    <XAxis
                      type="number"
                      stroke="#8ecbff"
                      allowDecimals={false}
                      domain={[0, (max) => Math.max(4, Math.ceil(max * 1.15))]}
                    />
                    <YAxis
                      type="category"
                      dataKey="difficulty"
                      stroke="#8ecbff"
                      width={110}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill={CHART_COLORS.difficulty} radius={[0, 6, 6, 0]} barSize={14}>
                      <LabelList dataKey="count" position="right" fill="#8ecbff" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="col-lg-12">
          <div className="card" style={cardStyle}>
            <h6 style={cardTitle}>By Location</h6>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.byLocation} margin={{ top: 18, right: 18, left: 0, bottom: 0 }}>
                <XAxis dataKey="location" stroke="#8ecbff" />
                <YAxis
                  stroke="#8ecbff"
                  allowDecimals={false}
                  domain={[0, (max) => Math.max(4, Math.ceil(max * 1.15))]}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill={CHART_COLORS.location}>
                  <LabelList dataKey="count" content={renderLocationCountLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* ---------------- PAGINATED CERTIFICATIONS TABLE ---------------- */}
      <div className="card mt-4" style={cardStyle}>
      <div
  className="d-flex justify-content-between align-items-center mb-2"
>
  <h6 className="mb-0" style={cardTitle}>
    All Certification Records ({filtered.length})
  </h6>

  <button
    onClick={exportToExcel}
    style={{
      background: "linear-gradient(135deg,#00c9ff,#007bff)",
      color: "#021322",
      fontWeight: 700,
      border: "none",
      borderRadius: 8,
      padding: "6px 14px",
      fontSize: 13,
      boxShadow: "0 0 0 1px rgba(0,201,255,0.35)",
    }}
  >
    ⬇ Export to Excel
  </button>
</div>
        
        <div style={{ overflowX: "auto", maxHeight: 450, overflowY: "auto" }} className="table-wrapper">

          <table className="table table-dark table-striped">
            <thead>
              <tr>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (sortBy === "name") setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    else { setSortBy("name"); setSortOrder("asc"); }
                    setPage(1);
                  }}
                >
                  Employee {sortBy === "name" && (sortOrder === "desc" ? "↓" : "↑")}
                </th>

                <th>Email</th>

                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (sortBy === "location") setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    else { setSortBy("location"); setSortOrder("asc"); }
                    setPage(1);
                  }}
                >
                  Location {sortBy === "location" && (sortOrder === "desc" ? "↓" : "↑")}
                </th>

                <th>Certificate</th>
                <th>Provider</th>

                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (sortBy === "status") setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    else { setSortBy("status"); setSortOrder("asc"); }
                    setPage(1);
                  }}
                >
                  Status {sortBy === "status" && (sortOrder === "desc" ? "↓" : "↑")}
                </th>

                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (sortBy === "points") setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    else { setSortBy("points"); setSortOrder("desc"); }
                    setPage(1);
                  }}
                >
                  Points {sortBy === "points" && (sortOrder === "desc" ? "↓" : "↑")}
                </th>

                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (sortBy === "completedDate") setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    else { setSortBy("completedDate"); setSortOrder("desc"); }
                    setPage(1);
                  }}
                >
                  Completed On {sortBy === "completedDate" && (sortOrder === "desc" ? "↓" : "↑")}
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedRecords.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">No records found.</td>
                </tr>
              )}

              {paginatedRecords.map((c) => {
                const meta = certMap[c.certId];
                return (
                  <tr key={c.employeeId + c.certId}>
                    <td>{c.name}</td>
                    <td>{c.email || c.employeeId || "-"}</td>
                    <td>{c.location}</td>
                    <td>{meta?.name || c.certId}</td>
                    <td>{meta?.provider || "-"}</td>
                    <td>
                      {c.status === "Completed" ? (
                        <span className="badge bg-success">Completed</span>
                      ) : c.status === "Ongoing" ? (
                        <span className="badge bg-info text-dark">Ongoing</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Registered</span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: "bold", color: "#00c9ff" }}>
                        {c.points}
                      </div>
                      {c.bonusPoints > 0 && (
                        <div style={{ fontSize: 11, color: "#8ecbff" }}>
                          +{c.bonusPoints} bonus (Rank #{c.completionRank})
                        </div>
                      )}
                    </td>
                    <td>{c.completedDate || "-"}</td>
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>

        {/* ----------- PAGINATION FOOTER ----------- */}
        <div className="d-flex justify-content-between align-items-center mt-3 pagination-row">
          <button
            className="btn btn-sm btn-outline-info"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            ◀ Previous
          </button>

          <div style={{ color: "#8ecbff", fontSize: 14 }}>
            Page {page} of {totalPages}
          </div>

          <button
            className="btn btn-sm btn-outline-info"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
          >
            Next ▶
          </button>
        </div>
      </div>
    </div>
    </div>
  );
}

function MultiSelectFilter({ allLabel, selectedValues, options, onChange, maxWidth = 220 }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function toggleValue(value) {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
      return;
    }
    onChange([...selectedValues, value]);
  }

  const buttonLabel = selectedValues.length
    ? `${selectedValues.length} selected`
    : allLabel;

  return (
    <div className="multi-select-filter" style={{ maxWidth }} ref={wrapperRef}>
      <button
        type="button"
        className="form-select multi-select-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        {buttonLabel}
      </button>

      {open && (
        <div className="multi-select-menu">
          <label className="multi-select-option">
            <input
              type="checkbox"
              checked={selectedValues.length === 0}
              onChange={() => onChange([])}
            />
            <span>{allLabel}</span>
          </label>

          {options.map((option) => (
            <label key={option.value} className="multi-select-option">
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => toggleValue(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function SingleSelectFilter({ value, options, onChange, maxWidth = 180 }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="multi-select-filter" style={{ maxWidth }} ref={wrapperRef}>
      <button
        type="button"
        className="form-select multi-select-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected?.label || "Select"}
      </button>

      {open && (
        <div className="multi-select-menu">
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`single-select-option ${value === option.value ? "selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="col-md-4">
      <div className="card" style={statCardStyle}>
        <div style={statTitle}>{title}</div>
        <div style={statValue}>{value}</div>
      </div>
    </div>
  );
}

const cardStyle = {
  borderRadius: 14,
  background: "rgba(3,19,40,0.95)",
  border: "1px solid rgba(130,200,255,0.35)",
  padding: 16,
};

const cardTitle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#8ecbff",
};

const statCardStyle = {
  ...cardStyle,
  textAlign: "center",
};

const statTitle = {
  fontSize: 13,
  color: "#8ecbff",
};

const statValue = {
  fontSize: 32,
  fontWeight: 800,
  color: "#eaf6ff",
};

function normalizeDifficulty(rawDifficulty) {
  return (rawDifficulty || "").toString().trim().toLowerCase();
}

function toTitleCase(value) {
  return (value || "")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function renderLocationCountLabel({ x, y, width, height, value }) {
  if (value == null) return null;

  const isShortBar = Number(height) < 24;
  const labelX = Number(x) + Number(width) / 2;
  const labelY = isShortBar ? Number(y) - 6 : Number(y) + 20;

  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor="middle"
      fill={isShortBar ? "#93c5fd" : "#e0f2fe"}
      fontSize={12}
      fontWeight={600}
    >
      {value}
    </text>
  );
}
